import { InputHTMLAttributes, useEffect, useState } from 'react';
import clsx from 'clsx';
import { parseARNumber, formatARNumber } from '@/utils/format';

interface CurrencyInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  value: string | number | undefined;
  onChange: (rawValue: string) => void;
  /** Cantidad de decimales a forzar al perder foco. Default 2. */
  decimals?: number;
  /** Sufijo a la derecha (ej: "/kg"). */
  suffix?: string;
  /** Color del foco (clases tailwind para ring/border). */
  focusRingClassName?: string;
  /** Estilo del wrapper del input. */
  wrapperClassName?: string;
}

/**
 * Input de moneda con prefijo $ y formato argentino (1.234,56).
 * Permite escribir libremente; al perder foco normaliza al formato AR.
 * `onChange` devuelve el string tal cual está (con separadores AR), para que
 * el formulario lo guarde como display string. Para enviar al backend usar
 * `parseARNumber` desde `@/utils/format`.
 */
export default function CurrencyInput({
  value,
  onChange,
  decimals = 2,
  suffix,
  focusRingClassName = 'focus:ring-taupe-400 focus:border-taupe-400',
  wrapperClassName,
  className,
  placeholder,
  disabled,
  ...rest
}: CurrencyInputProps) {
  // Estado local del display. Se sincroniza con value cuando cambia desde afuera.
  const [display, setDisplay] = useState<string>(() => normalizeToDisplay(value, decimals));

  useEffect(() => {
    // Si cambia el value externo, refrescar display (pero no mientras el usuario tipea
    // — para eso usamos comparación de equivalencia numérica).
    const incoming = normalizeToDisplay(value, decimals);
    const incomingNum = parseARNumber(incoming);
    const currentNum = parseARNumber(display);
    if (incomingNum !== currentNum) {
      setDisplay(incoming);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Permitir solo digitos, punto y coma
    const cleaned = e.target.value.replace(/[^0-9.,]/g, '');
    setDisplay(cleaned);
    onChange(cleaned);
  };

  const handleBlur = () => {
    const parsed = parseARNumber(display);
    if (parsed === null) {
      setDisplay('');
      onChange('');
      return;
    }
    const formatted = formatARNumber(parsed, decimals);
    setDisplay(formatted);
    onChange(formatted);
  };

  return (
    <div className={clsx('relative', wrapperClassName)}>
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-taupe-500 pointer-events-none text-sm font-medium">
        $
      </span>
      <input
        {...rest}
        type="text"
        inputMode="decimal"
        value={display}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={(e) => e.target.select()}
        disabled={disabled}
        placeholder={placeholder ?? '0,00'}
        className={clsx(
          'w-full pl-7 pr-3 py-2 border rounded-lg outline-none transition-all bg-white text-taupe-800 placeholder-taupe-400',
          focusRingClassName,
          'focus:ring-2',
          disabled && 'bg-taupe-50 text-taupe-400 cursor-not-allowed',
          className,
        )}
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-taupe-400 pointer-events-none">
          {suffix}
        </span>
      )}
    </div>
  );
}

function normalizeToDisplay(value: string | number | undefined, decimals: number): string {
  if (value === undefined || value === null || value === '') return '';
  if (typeof value === 'number') {
    return formatARNumber(value, decimals);
  }
  // Si ya está formateado como AR, devolverlo. Si es "1234.56" (US), parseARNumber lo entiende.
  const parsed = parseARNumber(value);
  if (parsed === null) return String(value);
  return formatARNumber(parsed, decimals);
}
